import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { useGetTagsQuery } from '@fiji/common/src/features/TagManagement/tagManagementApi';
import { setMessageContent } from '@fiji/common/src/features/common/commonSlice';
import { useUpdateDeviceMutation } from '@fiji/common/src/features/deviceManagement/deviceApi';
import { resetDeviceProperties } from '@fiji/common/src/features/deviceManagement/deviceSlice';
import { setRefetchGroupHierarchy } from '@fiji/common/src/features/group/groupSlice';
import { usePayloadActions } from '@fiji/common/src/hooks';
import { useNewCustomTranslations } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

type ReturnProps = {
    handleModalHeader: (args0: string) => string;
    handleDisable: (args0: any) => boolean;
    handleModalType: (args0: string) => string;
    handleSubmit: any;
    isLoading: boolean;
};

const hasSameTagNames = (tagsList: any[], payloadTags: any[]): boolean =>
    Boolean(
        payloadTags?.length === tagsList?.length &&
            payloadTags?.every(
                (payloadTag: string) => tagsList?.findIndex((tag: any) => tag?.name === payloadTag) !== -1
            )
    );

export const useHandleUpdate = (): ReturnProps => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { data: deviceTagsList } = useGetTagsQuery<{
        data: any;
    }>({
        page: 0,
        size: 100,
    });
    useNewCustomTranslations('DEVICE_PROPERTIES');
    const { deviceId } = useParams();

    const { propertiesPayload } = useTypedSelector((state) => state.device);

    const [updateDeviceProperties, { isLoading }] = useUpdateDeviceMutation();

    const [cleanPayload] = usePayloadActions();
    const dateFieldKeys = new Set(['batteryInstallDate', 'deviceInstallDate']);
    const refetchGroupHierarchyKeys = new Set(['groupId', 'name']);

    const isDescriptionUnchanged = (data: any): boolean =>
        data?.key === 'description' &&
        (data?.value === propertiesPayload?.value || (!propertiesPayload?.[data?.key]?.length && !data?.value));

    const isGroupLikeValueUnchanged = (data: any): boolean =>
        Boolean(
            data?.value &&
                (data?.value?.groupId ?? data?.value) ===
                    (propertiesPayload?.[data?.key]?.id ?? propertiesPayload[data?.key])
        );

    const isArrayValueUnchanged = (data: any): boolean =>
        Boolean(
            data?.value &&
                Array.isArray(data?.value) &&
                data?.value?.map((item: any) => item.id)?.toString() === propertiesPayload?.[data?.key]?.toString()
        );

    const areTagsUnchanged = (data: any): boolean => {
        if (data?.key !== 'tags') return false;
        const payloadTags = propertiesPayload?.[data?.key];
        if (hasSameTagNames(data?.value, payloadTags)) return true;
        return Boolean(data?.isDeviceTagUpdate && hasSameTagNames(data?.tags, payloadTags));
    };

    const isLocationUnchanged = (data: any): boolean =>
        Boolean(
            data?.key === 'location' &&
                data?.value &&
                (data?.value?.lat === propertiesPayload?.[data?.key]?.lat || !propertiesPayload?.[data?.key]?.lat) &&
                (data?.value?.lng === propertiesPayload?.[data?.key]?.lng || !propertiesPayload?.[data?.key]?.lng)
        );

    const handleDisable = (data: any): boolean =>
        isArrayValueUnchanged(data) ||
        isDescriptionUnchanged(data) ||
        isGroupLikeValueUnchanged(data) ||
        areTagsUnchanged(data) ||
        isLocationUnchanged(data);

    const handleModalHeader = (modalKey: string): string => {
        const modalHeader: any = {
            name: t('DEVICE_PROPERTIES:EDIT_NAME'),
            tags: t('DEVICE_PROPERTIES:EDIT_TAGS'),
            groupId: t('COMMON:EDIT_GROUP'),
            description: t('DEVICE_PROPERTIES:EDIT_DESCRIPTION'),
            location: t('DEVICE_PROPERTIES:EDIT_LOCATION'),
            batteryInstallDate: t('DEVICE_PROPERTIES:BATTERY_INSTALL_DATE'),
            deviceInstallDate: t('DEVICE_PROPERTIES:DEVICE_INSTALL_DATE'),
        };

        return modalHeader[modalKey] ?? '';
    };

    const handleModalType = (key: string): string => (dateFieldKeys.has(key) ? 'primary' : 'secondary');

    const getTagIdsFromPayloadNames = (payloadTagNames: string[]): any[] => {
        const payloadTagNameSet = new Set(payloadTagNames);

        return (
            deviceTagsList?.data?.records
                ?.filter((item: any) => payloadTagNameSet.has(item.name))
                ?.map((tag: any) => tag?.id)
                ?.filter((id: any) => id !== undefined && id !== null) ?? []
        );
    };

    const getNormalizedPayloadValue = (key: string): any => {
        const rawValue = propertiesPayload[key]?.id ?? propertiesPayload[key];
        const payloadValueTransformers: Record<string, (value: any) => any> = {
            batteryInstallDate: (value: any): number => new Date(value)?.getTime(),
            deviceInstallDate: (value: any): number => new Date(value)?.getTime(),
            tags: (value: string[]): any[] => getTagIdsFromPayloadNames(value),
        };

        const transformer = payloadValueTransformers[key];
        return transformer ? transformer(rawValue) : rawValue;
    };

    const dispatchSuccessState = (key: string): void => {
        dispatch(
            setMessageContent({
                isOpen: true,
                message: deviceId ? t('COMMON:CHANGES_SAVED_SUCCESS') : t('COMMON:TAGS_UPDATED_SUCCESSFULLY'),
            })
        );
        dispatch(resetDeviceProperties());
        if (refetchGroupHierarchyKeys.has(key)) {
            dispatch(setRefetchGroupHierarchy({ refetch: true }));
        }
    };

    const handleSubmit = async (key: string, callback: any, id?: string): Promise<void> => {
        const payload: any = { [key]: getNormalizedPayloadValue(key) };
        const cleanedPayload = cleanPayload({ payload, ...(payload?.location && { coordinates: payload?.location }) });

        const { error }: any = await updateDeviceProperties({
            deviceId: deviceId ?? id,
            body: cleanedPayload,
        });

        if (error) return;

        dispatchSuccessState(key);
        callback(true);
    };

    return {
        handleModalHeader,
        handleDisable,
        handleModalType,
        handleSubmit,
        isLoading,
    };
};
